import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, {
  CSSProperties,
  HTMLAttributeAnchorTarget,
  useCallback,
  useEffect,
  useId,
  useRef,
} from 'react'
import { useLocation } from 'react-router'

import ActionLink from '@/components/misc/ActionLink'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useCanScroll } from '@/hooks/useCanScroll'
import { faChevronLeft, faChevronRight } from '@/lib/fontAwesomePro/regular'
import { getCssVariable } from '@/lib/helpers/domHelpers'
import { cn } from '@/lib/utils'

export type ScrollableTabsProps = React.ComponentProps<'div'> & {
  tabs: ScrollableTab[]
  tabProps?: {
    className?: string
    style?: CSSProperties
  }
}

export type ScrollableTab = {
  to: string
  target?: HTMLAttributeAnchorTarget
  rel?: string
  label: React.ReactNode
  isActive?: boolean
  beforeNavigate?: () => void
  afterNavigate?: () => void
}

type ScrollDirection = 'right' | 'left'

const SCROLL_DIRECTION: Record<ScrollDirection, ScrollDirection> = {
  right: 'right',
  left: 'left',
}

const ScrollableTabs: React.FC<ScrollableTabsProps> = ({
  tabs,
  tabProps = {},
  className,
  style,
  ...props
}) => {
  const location = useLocation()
  const id = useId()
  const pathname = location.pathname
  const hash = location.hash
  const currentPath = pathname + hash
  const ref = useRef<HTMLDivElement | null>(null)
  const canScroll = useCanScroll(ref)
  const activeTab = tabs.find(isActiveTab)

  // If tab.isActive is defined on the tab, use that to determine if the tab is
  // active. Otherwise, check if the tab's `url` field matches the current URL.
  function isActiveTab(tab: ScrollableTab) {
    if (typeof tab.isActive === 'boolean') {
      return tab.isActive
    }

    return tab.to === currentPath
  }

  const activeTabUrl = activeTab?.to

  function scroll(direction: ScrollDirection) {
    const element = ref.current

    if (!element) {
      return
    }

    const scrollAmount = 70

    if (direction === SCROLL_DIRECTION.right) {
      element.scrollLeft += scrollAmount
    } else {
      element.scrollLeft -= scrollAmount
    }
  }

  const ACTIVE_TAB_ELEMENT_ID = 'active-scrollable-tab'
  const CONTAINER_ELEMENT_ID = `scrollable-tabs-${id}`
  const DIRECTION_INDICATOR_WIDTH = 50

  // Horizontally scroll the tabs container to ensure the active tab is visible
  const scrollToActiveTab = useCallback(() => {
    const tabsContainerEl = document.getElementById(CONTAINER_ELEMENT_ID)
    const activeTabEl = document.getElementById(ACTIVE_TAB_ELEMENT_ID)

    if (!tabsContainerEl || !activeTabEl) {
      console.warn('Failed to find tabsContainerEl or activeTabEl')
      return
    }

    // Get the number pixels the tab is offset from the parent element
    const tabOffsetLeft = activeTabEl.offsetLeft

    // Subtract the DIRECTION_INDICATOR_WIDTH to ensure the active tab isn't
    // partially covered bb the direction indicator
    tabsContainerEl.scrollLeft = tabOffsetLeft - DIRECTION_INDICATOR_WIDTH
  }, [CONTAINER_ELEMENT_ID])

  useEffect(() => {
    scrollToActiveTab()
  }, [activeTabUrl, scrollToActiveTab])

  return (
    <div className="relative h-full overflow-hidden">
      <ScrollIndicator
        direction="left"
        show={canScroll.left}
        onClick={() => scroll(SCROLL_DIRECTION.left)}
      />
      <nav
        className={cn(
          'scrollbar-none flex h-full items-center gap-2 overflow-x-scroll',
          className,
        )}
        ref={ref}
        style={{ borderBottom: '1px solid #E6E7EB', ...style }}
        id={CONTAINER_ELEMENT_ID}
        {...props}
      >
        {tabs.map((tab, index) => {
          const isFirstTab = index === 0
          const isActive = tab.to === activeTabUrl
          const isExternalLink = tab.to.startsWith('http')

          const className = cn(
            `shrink-0 text-md no-underline pr-4 text-body
            h-full text-sm`,
            {
              active: isActive,
              'pl-4': !isFirstTab,
            },
            tabProps.className,
          )

          const css = {
            borderBottom: '3px solid transparent',
            paddingBottom: '6px',
            '&.active, &:hover': {
              borderBottomColor: getCssVariable('--primary'),
            },
            ...tabProps.style,
          }

          return isExternalLink ? (
            <a
              key={index}
              href={tab.to.toString()}
              target={tab.target}
              rel={tab.rel}
              className={className}
              css={css}
            >
              {tab.label}
            </a>
          ) : (
            <ActionLink
              key={index}
              to={tab.to}
              id={isActive ? ACTIVE_TAB_ELEMENT_ID : undefined}
              beforeNavigate={tab.beforeNavigate}
              afterNavigate={tab.afterNavigate}
              className={className}
              css={css}
            >
              {tab.label}
            </ActionLink>
          )
        })}
      </nav>

      <ScrollIndicator
        direction="right"
        show={canScroll.right}
        onClick={() => scroll(SCROLL_DIRECTION.right)}
      />
    </div>
  )
}

const ScrollIndicator: React.FC<{
  direction: ScrollDirection
  show: boolean
  onClick: () => void
}> = ({ direction, show, onClick }) => {
  const isRightIndicator = direction === SCROLL_DIRECTION.right

  const styles: CSSProperties = {
    backgroundColor: 'rgba(255,255,255,.8)',
    width: show ? 'auto' : 0,
    opacity: show ? 0.9 : 0,
    transition: 'all .5s',
    top: 'calc(50% + 2px)',
    transform: 'translateY(-50%)',
  }

  if (isRightIndicator) {
    Object.assign(styles, {
      right: show ? 'calc(0% - 2px)' : '-9999px',
      boxShadow: `-5px 0 5px ${getCssVariable('--background')}`,
    })
  } else {
    Object.assign(styles, {
      left: show ? 0 : '-9999px',
      boxShadow: `5px 0 5px ${getCssVariable('--background')}`,
    })
  }

  return (
    <div
      className="bg-background fade-out absolute flex h-full flex-col
        items-center justify-center overflow-y-hidden"
      style={styles}
      onClick={onClick}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <FontAwesomeIcon
            icon={isRightIndicator ? faChevronRight : faChevronLeft}
            className="relative cursor-pointer px-4 hover:opacity-80"
            style={{ fontSize: '20px', top: '-5px' }}
          />
        </TooltipTrigger>

        <TooltipContent side="bottom">
          Scroll {direction} or click the arrow to see more
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

export default ScrollableTabs
